import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/utils/dateUtils";
import { applyInvalidations, INVALIDATES } from "../api/invalidates";
import { merchantFlowService } from "../api/merchantFlowService";
import { flowActionErrorMessage } from "./flowActionError";
import { downloadLinksQuery } from "./queries";

interface DownloadLinkListProps {
  borrowerId: number;
  flowId: number;
  /** False for a document: its links carry the one emailed PDF, not signed copies. */
  hasMerchantLink: boolean;
}

/** S11 rows with S13 revoke. Renders nothing until the flow has sent download links. */
const DownloadLinkList: React.FC<DownloadLinkListProps> = ({
  borrowerId,
  flowId,
  hasMerchantLink,
}) => {
  const queryClient = useQueryClient();
  const query = downloadLinksQuery(borrowerId, flowId);
  const links = useQuery(query);
  const revoke = useMutation({
    mutationFn: (linkId: number) =>
      merchantFlowService.revokeDownloadLink(borrowerId, flowId, linkId),
    onSuccess: ({ id, revoked_at }) => {
      queryClient.setQueryData(query.queryKey, (rows) =>
        rows?.map((row) => (row.id === id ? { ...row, revoked_at } : row))
      );
      applyInvalidations(queryClient, INVALIDATES.revokeLink(borrowerId, flowId));
    },
  });

  if (links.isError) {
    return (
      <p className="text-sm text-destructive">
        Could not load download links: {flowActionErrorMessage(links.error)}
      </p>
    );
  }
  if (!links.data?.length) return null;

  return (
    <section className="space-y-2">
      <h3 className="text-xs font-medium text-muted-foreground">
        {hasMerchantLink ? "Signed-copy download links" : "Document download links"}
      </h3>
      <ul className="divide-y rounded-md border">
        {links.data.map((link) => (
          <li key={link.id} className="flex items-center justify-between gap-3 px-3 py-2">
            <div className="min-w-0">
              <p className="truncate text-sm">{link.recipient_email}</p>
              <p className="text-xs text-muted-foreground">
                {link.revoked_at
                  ? `Revoked ${formatDateTime(link.revoked_at)}`
                  : `Sent ${formatDateTime(link.created_at)}`}
              </p>
            </div>
            {link.revoked_at ? null : (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                disabled={revoke.isPending && revoke.variables === link.id}
                aria-label={`Revoke download link for ${link.recipient_email}`}
                onClick={() => revoke.mutate(link.id)}
              >
                Revoke
              </Button>
            )}
          </li>
        ))}
      </ul>
      {revoke.isError ? (
        <p role="alert" className="text-xs text-destructive">
          {flowActionErrorMessage(revoke.error)}
        </p>
      ) : null}
    </section>
  );
};

export default DownloadLinkList;
